import {
  Button,
  FormNumber,
  FormPrice,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { PercentIcon } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { z } from "zod";
import {
  DOCUMENT_CURRENCY_SYMBOL,
  formatDocumentMoney,
  toMajorUnits,
  toMinorUnits,
} from "@/shared/lib/performance-document-currency";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import type { DocumentDTO } from "../../domain/contracts";
import {
  useTaxSettingsQuery,
  useUpdateDocumentBillDiscount,
  useUpdateDocumentTax,
} from "../api/queries";

const DocumentTaxFormSchema = z.object({
  taxEnabled: z.boolean(),
  taxRate: z.number().int().min(0).max(100),
});

const BillDiscountFormSchema = z.object({
  billDiscountAmount: z.number().min(0),
});

type DocumentTaxFormValues = z.infer<typeof DocumentTaxFormSchema>;
type BillDiscountFormValues = z.infer<typeof BillDiscountFormSchema>;

type DocumentTaxEditorProps = {
  eventId: string;
  document: DocumentDTO;
  canEdit: boolean;
};

export type DocumentTaxEditorHandle = {
  /** บันทึกส่วนลดที่ยังไม่ได้กด save ก่อนออกเอกสาร/พิมพ์ */
  savePendingChanges: () => Promise<void>;
};

function formatMoney(value: number, document: DocumentDTO) {
  return formatDocumentMoney(value, document.currency ?? "LAK");
}

export const DocumentTaxEditor = forwardRef<
  DocumentTaxEditorHandle,
  DocumentTaxEditorProps
>(function DocumentTaxEditor({ eventId, document, canEdit }, ref) {
  const taxSettings = useTaxSettingsQuery();
  const updateTax = useUpdateDocumentTax(eventId, document.id);
  const updateBillDiscount = useUpdateDocumentBillDiscount(
    eventId,
    document.id,
  );
  const defaultRate = taxSettings.data?.defaultTaxRate ?? 10;
  const currency = document.currency ?? "LAK";
  const symbol = DOCUMENT_CURRENCY_SYMBOL[currency];
  const billDiscountMajor = toMajorUnits(
    document.billDiscountAmount ?? 0,
    currency,
  );

  const taxMethods = RHF.useForm<DocumentTaxFormValues>({
    resolver: zodResolver(DocumentTaxFormSchema),
    defaultValues: {
      taxEnabled: document.taxEnabled ?? false,
      taxRate: document.taxRate || defaultRate,
    },
  });

  const discountMethods = RHF.useForm<BillDiscountFormValues>({
    resolver: zodResolver(BillDiscountFormSchema),
    defaultValues: {
      billDiscountAmount: billDiscountMajor,
    },
  });

  useEffect(() => {
    taxMethods.reset({
      taxEnabled: document.taxEnabled ?? false,
      taxRate: document.taxRate || defaultRate,
    });
  }, [
    document.id,
    document.taxEnabled,
    document.taxRate,
    defaultRate,
    taxMethods,
  ]);

  useEffect(() => {
    discountMethods.reset({
      billDiscountAmount: toMajorUnits(
        document.billDiscountAmount ?? 0,
        currency,
      ),
    });
  }, [document.id, document.billDiscountAmount, currency, discountMethods]);

  const taxEnabled = taxMethods.watch("taxEnabled");
  const taxRate = taxMethods.watch("taxRate");
  const watchedBillDiscount = discountMethods.watch("billDiscountAmount") ?? 0;

  const preview = useMemo(() => {
    const billDiscountMinor = Math.min(
      toMinorUnits(Math.max(0, watchedBillDiscount), currency),
      document.subtotal,
    );
    const netBeforeTax = Math.max(0, document.subtotal - billDiscountMinor);
    const previewTax = taxEnabled
      ? Math.round((netBeforeTax * (taxRate || 0)) / 100)
      : 0;
    return {
      billDiscountMinor,
      netBeforeTax,
      previewTax,
      previewGrand: netBeforeTax + previewTax,
    };
  }, [watchedBillDiscount, document.subtotal, taxEnabled, taxRate, currency]);

  useImperativeHandle(
    ref,
    () => ({
      savePendingChanges: async () => {
        if (!canEdit || !discountMethods.formState.isDirty) return;
        const values = discountMethods.getValues();
        await updateBillDiscount.mutateAsync({
          billDiscountAmount: values.billDiscountAmount,
        });
      },
    }),
    [canEdit, discountMethods, updateBillDiscount],
  );

  if (document.documentType === "receipt") return null;

  const summaryBlock = (
    <div className="rounded-lg border bg-muted/20 px-3 py-2 text-sm">
      <div className="flex justify-between gap-2">
        <span className="text-muted-foreground">ຍອດລວມລາຍການ</span>
        <span>{formatMoney(document.subtotal, document)}</span>
      </div>
      <div className="mt-1 flex justify-between gap-2">
        <span className="text-muted-foreground">ສ່ວນຫຼຸດທ້າຍບິນ</span>
        <span>
          {formatMoney(
            canEdit
              ? preview.billDiscountMinor
              : (document.billDiscountAmount ?? 0),
            document,
          )}
        </span>
      </div>
      <div className="mt-1 flex justify-between gap-2">
        <span className="text-muted-foreground">ຍອດກ່ອນພາສີ</span>
        <span>
          {formatMoney(
            canEdit
              ? preview.netBeforeTax
              : Math.max(
                  0,
                  document.subtotal - (document.billDiscountAmount ?? 0),
                ),
            document,
          )}
        </span>
      </div>
      <div className="mt-1 flex justify-between gap-2">
        <span className="text-muted-foreground">
          {canEdit
            ? `ພາສີ${taxEnabled ? ` ${taxRate}%` : ""}`
            : document.taxEnabled
              ? `ພາສີ ${document.taxRate}%`
              : "ບໍ່ໃສ່ພາສີ"}
        </span>
        <span>
          {formatMoney(
            canEdit ? preview.previewTax : document.taxAmount,
            document,
          )}
        </span>
      </div>
      <div className="mt-1 flex justify-between gap-2 font-semibold">
        <span>ຍອດລວມທັງໝົດ</span>
        <span>
          {formatMoney(
            canEdit ? preview.previewGrand : document.grandTotal,
            document,
          )}
        </span>
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border bg-card px-4 py-3">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <PercentIcon className="size-4" />
        </div>
        <div>
          <p className="font-semibold text-sm">ສ່ວນຫຼຸດທ້າຍບິນ & ພາສີ</p>
          <p className="text-muted-foreground text-xs">
            ຫັກສ່ວນຫຼຸດກ່ອນຄິດພາສີ — ໃສ່ໄດ້ຕັ້ງແຕ່ຮ່າງ ແລະ ຫຼັງອອກເອກະສານແລ້ວ
          </p>
        </div>
      </div>

      {canEdit ? (
        <div className="space-y-4">
          <FormRoot
            methods={discountMethods}
            onSubmit={(values) =>
              updateBillDiscount.mutate({
                billDiscountAmount: values.billDiscountAmount,
              })
            }
            className="space-y-3"
          >
            <FormPrice
              name="billDiscountAmount"
              label="ສ່ວນຫຼຸດທ້າຍບິນ"
              currencySymbol={symbol}
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                size="sm"
                variant="outline"
                disabled={updateBillDiscount.isPending}
              >
                {updateBillDiscount.isPending
                  ? "ກຳລັງບັນທຶກ..."
                  : "ບັນທຶກສ່ວນຫຼຸດ"}
              </Button>
            </div>
          </FormRoot>

          <FormRoot
            methods={taxMethods}
            onSubmit={(values) =>
              updateTax.mutate({
                taxEnabled: values.taxEnabled,
                taxRate: values.taxEnabled ? values.taxRate : 0,
              })
            }
            className="space-y-3"
          >
            <FormCheckboxCard
              name="taxEnabled"
              label="ໃສ່ພາສີໃນເອກະສານນີ້"
              description={`ຄ່າເລີ່ມຕົ້ນລະບົບ ${defaultRate}% — ສະຫຼັບເງື່ອນໄຂການຊຳລະຕາມແມ່ແບບທີ່ກົງກັນ`}
            />
            {taxEnabled ? (
              <FormNumber
                name="taxRate"
                label="ອັດຕາພາສີ (%)"
                min={0}
                max={100}
              />
            ) : null}
            {summaryBlock}
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={updateTax.isPending}>
                {updateTax.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກພາສີ"}
              </Button>
            </div>
          </FormRoot>
        </div>
      ) : (
        summaryBlock
      )}
    </div>
  );
});
